import { Card, Segmented, Space, Typography, Skeleton, Button, Dropdown } from "antd";
import { BarChartOutlined, PieChartOutlined, LineChartOutlined, DownloadOutlined } from "@ant-design/icons";
import { useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { COLORS } from "../../../utils/constant";
import { useCpdDimensionChartData } from "../../../hooks/cpd";
import { exportChartAsPDF } from "../../../utils/export.utils";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import * as XLSX from "xlsx";

const { Title, Text } = Typography;

const CpdDimensionPanel = ({ 
  dimensionData, 
  activeDimension, 
  onDimensionChange, 
  loading,
  dateRange,
}) => {
  const [chartType, setChartType] = useState("bar");
  const chartRef = useRef();
  const { username } = useAuthUser();
  
  const { chartData, totalUsers, totalPoints } = useCpdDimensionChartData(
    dimensionData,
    activeDimension
  );

  const dimensionOptions = [
    { label: "Designation", value: "designation" },
    { label: "Gender", value: "gender" },
    { label: "Hospital", value: "hospital" },
    { label: "District", value: "district" },
  ];

  const getChartOption = () => {
    if (!chartData?.length) {
      return {
        backgroundColor: "#ffffff",
        title: { text: "No data available", left: "center", top: "middle" },
      };
    }

    const categories = chartData.map(item => item.name);

    if (chartType === "pie") {
      return {
        backgroundColor: "#ffffff",
        title: {
          text: `CPD Distribution by ${activeDimension}`,
          left: "center",
          textStyle: { fontSize: 16, fontWeight: "bold" },
        },
        tooltip: {
          trigger: "item",
          formatter: (params) => {
            return `
              <strong>${params.name}</strong><br/>
              Users: ${params.data.userCount.toLocaleString()}<br/>
              CPD Points: ${params.data.totalCpdPoints.toLocaleString()}<br/>
              Percentage: ${params.percent.toFixed(1)}%
            `;
          },
        },
        legend: {
          type: "scroll",
          orient: "vertical",
          right: 10,
          top: 50,
          bottom: 20,
        },
        series: [
          {
            name: "Users",
            type: "pie",
            radius: ["40%", "70%"],
            center: ["40%", "50%"],
            data: chartData.map((item, index) => ({
              name: item.name,
              value: item.userCount,
              userCount: item.userCount,
              totalCpdPoints: item.totalCpdPoints,
              itemStyle: { color: COLORS[index % COLORS.length] },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
            label: {
              formatter: "{b}: {c} ({d}%)",
            },
          },
        ],
      };
    }

    return {
      backgroundColor: "#ffffff",
      title: {
        text: `CPD Metrics by ${activeDimension}`,
        left: "center",
        textStyle: { fontSize: 16, fontWeight: "bold" },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          let tooltip = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((param) => {
            tooltip += `${param.marker} ${param.seriesName}: ${param.value?.toLocaleString() || 0}<br/>`;
          });
          return tooltip;
        },
      },
      legend: {
        data: ["User Count", "Completed In Depth Learning", "Completed Cases", "Completed Guidelines", "Total CPD Points"],
        top: 40,
        type: "scroll",
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "10%",
        top: 100,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLabel: {
          rotate: categories.length > 5 ? 45 : 0,
          interval: 0,
        },
      },
      yAxis: {
        type: "value",
        name: "Count",
      },
      series: [
        {
          name: "User Count",
          type: chartType,
          data: chartData.map(item => item.userCount),
          itemStyle: { color: COLORS[0] },
          emphasis: { focus: "series" },
        },
        {
          name: "Completed In Depth Learning",
          type: chartType,
          data: chartData.map(item => item.completedQuizzes),
          itemStyle: { color: COLORS[1] },
          emphasis: { focus: "series" },
        },
        {
          name: "Completed Cases",
          type: chartType,
          data: chartData.map(item => item.completedCases),
          itemStyle: { color: COLORS[2] },
          emphasis: { focus: "series" },
        },
        {
          name: "Completed Guidelines",
          type: chartType,
          data: chartData.map(item => item.completedGuidelines),
          itemStyle: { color: COLORS[3] },
          emphasis: { focus: "series" },
        },
        {
          name: "Total CPD Points",
          type: chartType,
          data: chartData.map(item => item.totalCpdPoints),
          itemStyle: { color: COLORS[4] },
          emphasis: { focus: "series" },
        },
      ],
    };
  };

  const handleExport = async (format) => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) {
      console.error("Chart instance not available for export");
      return;
    }

    const filename = `cpd-${activeDimension}-${username}-${Date.now()}`;

    try {
      if (format === "png") {
        const url = chartInstance.getDataURL({ 
          type: "png", 
          pixelRatio: 2,
          backgroundColor: "#ffffff" 
        });
        const link = document.createElement("a");
        link.href = url;
        link.download = `${filename}.png`;
        link.click();
      } else if (format === "pdf") {
        await exportChartAsPDF({
          chartElement: chartInstance,
          title: `CPD Analytics by ${activeDimension}`,
          footnote: `${dateRange?.[0]?.format("DD MMM YYYY")} - ${dateRange?.[1]?.format("DD MMM YYYY")}`,
          filename: `${filename}.pdf`,
        });
      } else if (format === "excel") {
        handleExportExcel();
      }
    } catch (error) {
      console.error(`Error exporting ${format}:`, error);
    }
  };

  const handleExportExcel = () => {
    if (!chartData?.length) return;

    const exportData = chartData.map((item) => ({
      [activeDimension.charAt(0).toUpperCase() + activeDimension.slice(1)]: item.name,
      "User Count": item.userCount,
      "Completed In Depth Learning": item.completedQuizzes,
      "Completed Cases": item.completedCases,
      "Completed Guidelines": item.completedGuidelines,
      "Total CPD Points": item.totalCpdPoints,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, `CPD by ${activeDimension}`);

    // Auto-size columns
    const maxWidth = 30;
    const colWidths = Object.keys(exportData[0] || {}).map((key) => {
      const maxLen = Math.max(
        key.length,
        ...exportData.map((row) => String(row[key] || "").length)
      );
      return { wch: Math.min(maxLen + 2, maxWidth) };
    });
    worksheet["!cols"] = colWidths;

    const filename = `cpd-${activeDimension}-${username}-${Date.now()}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const exportMenuItems = [
    {
      key: "png",
      label: "Export as PNG",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("png"),
    },
    {
      key: "pdf",
      label: "Export as PDF",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("pdf"),
    },
    {
      key: "excel",
      label: "Export as Excel",
      icon: <DownloadOutlined />,
      onClick: () => handleExport("excel"),
    },
  ];

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Title level={4} style={{ margin: 0 }}>
            CPD Activity by Dimension
          </Title>
          <Space size="small">
            <Text type="secondary">Total Users: {totalUsers.toLocaleString()}</Text>
            <Text type="secondary">•</Text>
            <Text type="secondary">Total Points: {totalPoints.toLocaleString()}</Text>
          </Space>
        </Space>
      }
      extra={
        <Space>
          <Segmented
            value={chartType}
            onChange={setChartType}
            options={[
              { label: <BarChartOutlined />, value: "bar" },
              { label: <LineChartOutlined />, value: "line" },
              { label: <PieChartOutlined />, value: "pie" },
            ]}
          />
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button icon={<DownloadOutlined />}>Export</Button>
          </Dropdown>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Segmented
          value={activeDimension}
          onChange={onDimensionChange}
          options={dimensionOptions}
          block
        />

        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <ReactECharts
            ref={chartRef}
            option={getChartOption()}
            style={{ height: "500px", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
          />
        )}
      </Space>
    </Card>
  );
};

export default CpdDimensionPanel;
